import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { format, addDays, isBefore, startOfToday } from "date-fns";
import { hu } from "date-fns/locale";
import { CalendarIcon, Clock, Car, User, Phone, Mail, MessageSquare, CheckCircle } from "lucide-react";
import { Button } from "../components/ui/button";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [loadingTimes, setLoadingTimes] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    date: null,
    time: "",
    car_brand: "",
    car_model: "",
    message: ""
  });

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data.services);
      } catch (error) {
        console.error("Error fetching services:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    const fetchAvailableTimes = async () => {
      if (formData.date) {
        setLoadingTimes(true);
        try {
          const dateStr = format(formData.date, "yyyy-MM-dd");
          const response = await axios.get(`${API}/bookings/available-times?date=${dateStr}`);
          setAvailableTimes(response.data.available_times);
        } catch (error) {
          console.error("Error fetching available times:", error);
          setAvailableTimes(["08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00"]);
        } finally {
          setLoadingTimes(false);
        }
      }
    };
    fetchAvailableTimes();
  }, [formData.date]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({ ...prev, date, time: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.service || !formData.date || !formData.time) {
      toast.error("Kérjük, töltse ki az összes kötelező mezőt!");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...formData,
        date: format(formData.date, "yyyy-MM-dd")
      });
      setSubmitted(true);
      toast.success("Foglalás sikeresen elküldve!");
    } catch (error) {
      console.error("Error submitting booking:", error);
      toast.error("Hiba történt a foglalás elküldése közben. Kérjük, próbálja újra.");
    } finally {
      setSubmitting(false);
    }
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const today = startOfToday();
  const disabledDays = { before: today };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (submitted) {
    return (
      <div data-testid="booking-success" className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md text-center"
        >
          <div className="w-20 h-20 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-black" />
          </div>
          <h2 className="font-heading text-3xl text-[#E5E5E5] mb-4">FOGLALÁS ELKÜLDVE!</h2>
          <p className="text-[#A3A3A3] mb-6">
            Köszönjük foglalását! Hamarosan felvesszük Önnel a kapcsolatot a megadott elérhetőségeken a foglalás megerősítéséhez.
          </p>
          <div className="bg-[#121212] border border-[#2A2A2A] p-6 text-left mb-6">
            <h4 className="text-[#D4AF37] font-heading mb-4">Foglalás részletei:</h4>
            <div className="space-y-2 text-sm">
              <p><span className="text-[#666]">Név:</span> <span className="text-[#E5E5E5]">{formData.name}</span></p>
              <p><span className="text-[#666]">Szolgáltatás:</span> <span className="text-[#E5E5E5]">{formData.service}</span></p>
              <p><span className="text-[#666]">Dátum:</span> <span className="text-[#E5E5E5]">{format(formData.date, "yyyy. MMMM d.", { locale: hu })}</span></p>
              <p><span className="text-[#666]">Időpont:</span> <span className="text-[#E5E5E5]">{formData.time}</span></p>
            </div>
          </div>
          <Button
            onClick={() => {
              setSubmitted(false);
              setFormData({
                name: "", email: "", phone: "", service: "",
                date: null, time: "", car_brand: "", car_model: "", message: ""
              });
            }}
            className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest hover:bg-[#B5952F] px-8 py-4 rounded-sm"
          >
            Új Foglalás
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="booking-page" className="bg-[#050505]">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="container mx-auto px-6">
          <motion.div {...fadeInUp} className="max-w-3xl">
            <span className="text-[#D4AF37] text-sm uppercase tracking-widest mb-4 block">
              Időpontfoglalás
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl text-[#E5E5E5] mb-6">
              FOGLALJON IDŐPONTOT
              <span className="block text-[#D4AF37]">ONLINE</span>
            </h1>
            <p className="text-[#A3A3A3] text-lg leading-relaxed">
              Válassza ki a megfelelő szolgáltatást, dátumot és időpontot. 
              Munkatársunk hamarosan felveszi Önnel a kapcsolatot.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <motion.form
              {...fadeInUp}
              onSubmit={handleSubmit}
              className="bg-[#121212] border border-[#2A2A2A] p-8 md:p-12"
              data-testid="booking-form"
            >
              {/* Personal Info */}
              <div className="mb-10">
                <h3 className="font-heading text-xl text-[#D4AF37] mb-6 flex items-center gap-2">
                  <User size={20} /> Személyes adatok
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-[#A3A3A3] mb-2 block">Név *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Teljes név"
                      data-testid="input-name"
                      className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#D4AF37] text-[#E5E5E5] placeholder:text-[#666]"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-[#A3A3A3] mb-2 block">Telefon *</Label>
                    <div className="relative">
                      <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666]" />
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+36 30 123 4567"
                        data-testid="input-phone"
                        className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#D4AF37] text-[#E5E5E5] placeholder:text-[#666] pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="email" className="text-[#A3A3A3] mb-2 block">Email *</Label>
                    <div className="relative">
                      <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666]" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="pelda@email.com"
                        data-testid="input-email"
                        className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#D4AF37] text-[#E5E5E5] placeholder:text-[#666] pl-10"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Service Selection */}
              <div className="mb-10">
                <h3 className="font-heading text-xl text-[#D4AF37] mb-6 flex items-center gap-2">
                  <Car size={20} /> Szolgáltatás és autó
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <Label className="text-[#A3A3A3] mb-2 block">Szolgáltatás *</Label>
                    <Select onValueChange={(value) => handleSelectChange("service", value)}>
                      <SelectTrigger 
                        className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#D4AF37] text-[#E5E5E5]"
                        data-testid="select-service"
                      >
                        <SelectValue placeholder="Válasszon szolgáltatást" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                        {services.map((service) => (
                          <SelectItem 
                            key={service.id} 
                            value={service.name}
                            className="text-[#E5E5E5] focus:bg-[#1E1E1E] focus:text-[#D4AF37]"
                          >
                            {service.name} - {service.price_from.toLocaleString()} Ft-tól
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="car_brand" className="text-[#A3A3A3] mb-2 block">Autó márkája</Label>
                    <Input
                      id="car_brand"
                      name="car_brand"
                      value={formData.car_brand}
                      onChange={handleInputChange}
                      placeholder="pl. BMW"
                      data-testid="input-car-brand"
                      className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#D4AF37] text-[#E5E5E5] placeholder:text-[#666]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="car_model" className="text-[#A3A3A3] mb-2 block">Autó típusa</Label>
                    <Input
                      id="car_model"
                      name="car_model"
                      value={formData.car_model}
                      onChange={handleInputChange}
                      placeholder="pl. M5 Competition"
                      data-testid="input-car-model"
                      className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#D4AF37] text-[#E5E5E5] placeholder:text-[#666]"
                    />
                  </div>
                </div>
              </div>

              {/* Date & Time */}
              <div className="mb-10">
                <h3 className="font-heading text-xl text-[#D4AF37] mb-6 flex items-center gap-2">
                  <CalendarIcon size={20} /> Dátum és időpont
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-[#A3A3A3] mb-2 block">Dátum *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          data-testid="date-picker-trigger"
                          className={`w-full justify-start text-left bg-[#0A0A0A] border-[#2A2A2A] hover:bg-[#1E1E1E] hover:border-[#D4AF37] ${
                            formData.date ? "text-[#E5E5E5]" : "text-[#666]"
                          }`}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.date ? format(formData.date, "yyyy. MMMM d.", { locale: hu }) : "Válasszon dátumot"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-[#121212] border-[#2A2A2A]" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.date}
                          onSelect={handleDateChange}
                          disabled={disabledDays}
                          locale={hu}
                          data-testid="calendar"
                          className="bg-[#121212]"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-[#A3A3A3] mb-2 block">Időpont *</Label>
                    <Select 
                      onValueChange={(value) => handleSelectChange("time", value)}
                      disabled={!formData.date || loadingTimes}
                    >
                      <SelectTrigger 
                        className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#D4AF37] text-[#E5E5E5] disabled:opacity-50"
                        data-testid="select-time"
                      >
                        <Clock className="mr-2 h-4 w-4 text-[#666]" />
                        <SelectValue placeholder={loadingTimes ? "Betöltés..." : "Válasszon időpontot"} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-[#2A2A2A]">
                        {availableTimes.map((time) => (
                          <SelectItem 
                            key={time} 
                            value={time}
                            className="text-[#E5E5E5] focus:bg-[#1E1E1E] focus:text-[#D4AF37]"
                          >
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Message */}
              <div className="mb-10">
                <h3 className="font-heading text-xl text-[#D4AF37] mb-6 flex items-center gap-2">
                  <MessageSquare size={20} /> Megjegyzés
                </h3>
                <Textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  placeholder="Írja le egyedi igényeit, vagy osszon meg bármilyen fontos információt..."
                  rows={4}
                  data-testid="input-message"
                  className="bg-[#0A0A0A] border-[#2A2A2A] focus:border-[#D4AF37] text-[#E5E5E5] placeholder:text-[#666]"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={submitting}
                data-testid="submit-booking"
                className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest hover:bg-[#B5952F] py-6 rounded-sm text-base disabled:opacity-50"
              >
                {submitting ? "Foglalás küldése..." : "Időpont Foglalása"}
              </Button>
            </motion.form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
